// /var/www/oszczedzam/client/src/components/goals/GoalsList.js
import React, { useState } from 'react';
import { 
  Box, Grid, Typography, FormControl, InputLabel, 
  Select, MenuItem, TextField, InputAdornment, IconButton,
  Divider, Paper, Tabs, Tab, Chip
} from '@mui/material';
import { 
  Search as SearchIcon,
  Sort as SortIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import GoalCard from './GoalCard';

function GoalsList({ goals, onEdit, onDelete, onAddSavings }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('progress');
  const [filterBy, setFilterBy] = useState('all');
  const [tabValue, setTabValue] = useState(0); // 0: all, 1: active, 2: completed

  // Filtrowanie celów wg aktualnego taba
  const filterByTab = (goals) => {
    switch (tabValue) {
      case 1: // aktywne
        return goals.filter(goal => goal.currentAmount < goal.targetAmount);
      case 2: // zrealizowane
        return goals.filter(goal => goal.currentAmount >= goal.targetAmount);
      default: // wszystkie
        return goals;
    }
  };

  // Filtrowanie celów wg kategorii
  const filterByCategory = (goals) => {
    if (filterBy === 'all') return goals;
    return goals.filter(goal => goal.category === filterBy);
  };

  // Filtrowanie celów wg wyszukiwania
  const filterBySearch = (goals) => {
    if (!searchTerm.trim()) return goals;
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return goals.filter(goal => 
      goal.name.toLowerCase().includes(lowerSearchTerm) || 
      goal.description?.toLowerCase().includes(lowerSearchTerm)
    );
  };

  // Sortowanie celów
  const sortGoals = (goals) => {
    switch (sortBy) {
      case 'nameAsc':
        return [...goals].sort((a, b) => a.name.localeCompare(b.name));
      case 'nameDesc':
        return [...goals].sort((a, b) => b.name.localeCompare(a.name));
      case 'amountAsc':
        return [...goals].sort((a, b) => a.targetAmount - b.targetAmount);
      case 'amountDesc':
        return [...goals].sort((a, b) => b.targetAmount - a.targetAmount);
      case 'dateAsc':
        return [...goals].sort((a, b) => {
          if (!a.targetDate && !b.targetDate) return 0;
          if (!a.targetDate) return 1;
          if (!b.targetDate) return -1;
          return new Date(a.targetDate) - new Date(b.targetDate);
        });
      case 'dateDesc':
        return [...goals].sort((a, b) => {
          if (!a.targetDate && !b.targetDate) return 0;
          if (!a.targetDate) return 1;
          if (!b.targetDate) return -1;
          return new Date(b.targetDate) - new Date(a.targetDate);
        });
      case 'progress':
        return [...goals].sort((a, b) => {
          const progressA = (a.currentAmount / a.targetAmount) * 100;
          const progressB = (b.currentAmount / b.targetAmount) * 100;
          return progressB - progressA; // Od największego postępu
        });
      case 'priority':
        // Sortuj od najwyższego priorytetu
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return [...goals].sort((a, b) => 
          priorityOrder[b.priority] - priorityOrder[a.priority]
        );
      default:
        return goals;
    }
  };

  // Pobierz wszystkie unikalne kategorie z celów
  const getUniqueCategories = () => {
    return [...new Set(goals.map(goal => goal.category))];
  };

  // Obsługa zmiany zakładki
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Zastosuj wszystkie filtry i sortowanie
  const processedGoals = sortGoals(filterBySearch(filterByCategory(filterByTab(goals))));

  // Podsumowanie statusu
  const completedGoals = goals.filter(goal => goal.currentAmount >= goal.targetAmount).length;
  const inProgressGoals = goals.length - completedGoals;

  return (
    <Box>
      {/* Statystyki */}
      {goals.length > 0 && (
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Chip 
            label={`Wszystkie cele: ${goals.length}`} 
            color="primary"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          <Chip 
            label={`W trakcie: ${inProgressGoals}`} 
            color="warning"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          <Chip 
            label={`Zrealizowane: ${completedGoals}`} 
            color="success"
            variant="outlined"
          />
        </Box>
      )}
      
      {/* Filtry i wyszukiwanie */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              label="Szukaj"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="sort-label">Sortuj według</InputLabel>
              <Select
                labelId="sort-label"
                id="sort-select"
                value={sortBy}
                label="Sortuj według"
                onChange={(e) => setSortBy(e.target.value)}
                startAdornment={<SortIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="progress">Postęp (malejąco)</MenuItem>
                <MenuItem value="nameAsc">Nazwa (A-Z)</MenuItem>
                <MenuItem value="nameDesc">Nazwa (Z-A)</MenuItem>
                <MenuItem value="amountAsc">Kwota (rosnąco)</MenuItem>
                <MenuItem value="amountDesc">Kwota (malejąco)</MenuItem>
                <MenuItem value="dateAsc">Data (najwcześniej)</MenuItem>
                <MenuItem value="dateDesc">Data (najpóźniej)</MenuItem>
                <MenuItem value="priority">Priorytet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-label">Filtruj kategorię</InputLabel>
              <Select
                labelId="filter-label"
                id="filter-select"
                value={filterBy}
                label="Filtruj kategorię"
                onChange={(e) => setFilterBy(e.target.value)}
                startAdornment={<FilterListIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">Wszystkie kategorie</MenuItem>
                {getUniqueCategories().map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Zakładki */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Wszystkie" />
          <Tab label="W trakcie" />
          <Tab label="Zrealizowane" />
        </Tabs>
      </Box>
      
      {/* Lista celów */}
      {processedGoals.length > 0 ? (
        <Grid container spacing={3}>
          {processedGoals.map((goal) => (
            <Grid item xs={12} sm={6} md={4} key={goal.id}>
              <GoalCard 
                goal={goal}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddSavings={onAddSavings}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            {searchTerm || filterBy !== 'all' || tabValue !== 0
              ? 'Brak celów spełniających kryteria wyszukiwania.'
              : 'Nie masz jeszcze żadnych celów oszczędnościowych.'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default GoalsList;